import { ReactElement, useEffect } from 'react';
import { ADD_TO_CART_DISPLAY_TYPE, AddToCart } from '@client/cart/components/AddToCart';
import { FACET_FILTERS, FILTERS } from '@client/catalog/utils/facets';
import { useDeviceType } from '@client/common/hooks';
import { stringify } from '@client/common/utils/queryParams';
import { useTranslate } from '@client/i18n/hooks';
import { ProductActions } from '@client/product/components/ProductActions';
import { BulkPrices } from '@client/product/components/ProductData/BulkPrices/BulkPrices';
import { BulkPricesV2 } from '@client/product/components/ProductData/BulkPricesV2';
import { Features } from '@client/product/components/ProductData/Features';
import { PriceList } from '@client/product/components/ProductData/PriceList';
import { Props } from '@client/product/components/ProductData/ProductData';
import { ProductVariants } from '@client/product/components/ProductData/ProductVariants';
import { useGetEnergyLabelImageBySku } from '@client/product/hooks/useGetEnergyLabelImageBySku';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useUrlCreator } from '@client/routes/hooks';
import { getCdnImageUrl } from '@client/routes/utils/url';
import { useStickyFooter } from 'b2b-components/context/StickyFooterContext';
import { PRODUCT_LABEL_SIZE } from 'b2b-components/core/components/ProductLabel/ProductLabel/ProductLabel';
import { AttributesList } from 'b2b-components/theme//components/AttributesList';
import { EnergyLabel } from 'b2b-components/theme/components/EnergyLabel';
import { Icon, ICON_NAME, ICON_SIZE } from 'b2b-components/theme/components/Icon';
import { LabelList } from 'b2b-components/theme/components/ProductList/ProductBox/common/LabelList';
import { isMobile } from 'b2b-components/utils/device';
import { USBCharger } from 'b2b-apps/core/product/components/ProductData/USBCharger';
import { getUSBCDataFromSpecifications } from 'b2b-apps/core/product/utils/getUSBCDataFromSpecifications';
import './ProductData.scss';

export const ProductData = ({ product, onAddToCart }: Props): ReactElement => {
  const t = useTranslate();
  const urlCreator = useUrlCreator();
  const { setIsStickyFooterEnabled } = useStickyFooter() || {};
  const deviceType = useDeviceType();
  const {
    effectivePricing,
    description: { energyLabel, specification },
    settings,
    sku,
    variants,
  } = product;

  const energyLabelImage = useGetEnergyLabelImageBySku(sku);
  const energyLabelImageUrl = getCdnImageUrl(energyLabelImage?.sizes?.full);
  const energyLabelText = {
    close: t('close'),
    energyLabel: t('Energy Label'),
  };
  const usbCData = getUSBCDataFromSpecifications(specification);


  const {
    isNew,
    isOccasion,
    isSellout,
    isSpecialOffer,
    isCoreAssortment,
    isOwnAssortment,
    isConsciousAlternative,
  } = settings;

  const translation = product.description;
  const manufacturerName = product.manufacturer.name;
  const facetFilter = stringify({
    [FILTERS.facetFilters]: {
      [FACET_FILTERS.manufacturer]: [manufacturerName],
    },
  });
  const manufacturerLinkUrl = `${urlCreator(ROUTES.brand)}?${facetFilter}`;
  const boughtLabel = product?.bought
    ? t(
      [
        'You have previously bought this item on {date} (×1 pc.)',
        'You have previously bought this item on {date} (×{quantity} pcs.)',
        'quantity',
      ],
      {
        quantity: product.bought.quantity,
        date: product.bought.date,
      },
    )
    : null;

  useEffect(() => {
    if (isMobile(deviceType)) {
      setIsStickyFooterEnabled?.(true);
    }

    return () => {
      setIsStickyFooterEnabled?.(false);
    };
  }, []);

  return (
    <div className='ProductData'>
      <LabelList
        size={PRODUCT_LABEL_SIZE.small}
        className='ProductData-Labels'
        isNew={isNew}
        isOccasion={isOccasion}
        isSellout={isSellout}
        isPromotion={isSpecialOffer}
        isCoreAssortment={isCoreAssortment}
        isOwnAssortment={isOwnAssortment}
        isConsciousAlternative={isConsciousAlternative}
        text={{
          new: t('new flag', {}, 'Product flag icon - "new" label'),
          occasion: t('verified return flag', {}, 'Product flag icon - "verified return" label'),
          promotion: t('special offer flag', {}, 'Product flag icon - "special offer" label'),
          sellout: t('sale flag', {}, 'Product flag icon - "sale" label'),
          coreAssortment: t('Core assortment'),
          ownAssortment: t('Customer specific assortment'),
          consciousAlternative: t('Conscious alternative'),
        }}
      />
      <a
        className='cds-Link'
        href={manufacturerLinkUrl}
      >
        {product.manufacturer.name}
      </a>
      <h1 className="ProductData-Title">{translation.title}</h1>
      {!!translation.subtitle && <h6 className="ProductData-Subtitle">{translation.subtitle}</h6>}

      {energyLabel && (
        <EnergyLabel
          className='ProductData-EnergyLabel'
          range={energyLabel.range}
          rating={energyLabel.value}
          energyLabelUrl={energyLabelImageUrl}
          text={energyLabelText}
        />
      )}

      <div className='ProductData-ActionsMobile'>
        <PriceList
          effectivePricing={effectivePricing}
          className='ProductData-Pricelist'
        />
        <ProductActions
          className='order-0 order-sm-1'
          product={product}
        />
      </div>

      {product.bought && (
        <div className='ProductData-Bought'>
          <Icon className='ProductData-Bought-Icon' name={ICON_NAME.boxCheck} size={ICON_SIZE.small} />
          <span className='ProductData-Bought-Label'>{boughtLabel}</span>
        </div>
      )}

      <div className='ProductData-ActionsSection'>
        <BulkPricesV2 />
        <div className='ProductData-ActionsWrapper'>
          <div
            className='ProductData-Buy'
            data-testid='addToCartPDP'
          >
            <AddToCart
              type={ADD_TO_CART_DISPLAY_TYPE.full}
              sku={product.sku}
              productName={translation.title}
              settings={product.settings}
              isUserCertified={effectivePricing.isCertified}
              onClick={onAddToCart}
              dataAttributes={{ 'data-testid': 'pdpAddToCartButton' }}
            />
          </div>
          <ProductActions
            className='ProductData-HideActions order-0 order-sm-1'
            product={product}
          />
        </div>
      </div>

      <BulkPrices
        className="ProductData-BulkPrices--Desktop"
        discounts={product.scaledDiscounts}
      />

      <BulkPrices
        className="ProductData-BulkPrices--Mobile"
        discounts={product.scaledDiscounts}
      />

      <AttributesList attributes={translation.bulletPoints} withPadding className='ProductData-Attributes' />

      <Features
        sku={product.sku}
        ean={product.ean}
        manufacturerProductId={product.manufacturer.productId}
        manufacturerUrl={product.manufacturer.url}
        manufacturerName={product.manufacturer.name}
        manufacturerWarranty={product.manufacturer.warranty}
        manufacturerInternalPage={product.manufacturer.internalPage}
        soldSince={product.webDateStart}
        isSpecialOrder={product.settings.isSpecialOrder}
        quantityOrdered={product.settings.quantityOrdered}
        stockCount={product.settings.quantity}
        directDelivery={product.settings.directDelivery}
        isOccasion={isOccasion}
        isSellout={isSellout}
      />

      {usbCData.hasUsbCPort && <USBCharger {...usbCData} />}

      {!!variants?.length
        && <div className='ProductVariants-Wrapper'>
          <ProductVariants sku={sku} variantGroups={variants} />
        </div>
      }
    </div>
  );
};
